import { getMeetingTokens } from '../infrastructure/api-hca'
import { useCallback, useState } from 'react'
import { useTimeline } from './useTimeline'
import { useTranslation } from 'react-i18next'
import { useUIContext } from '../context/UIContext'
import { useUserCourses } from './queries/useUserCourses'
import { useToken } from './useToken'
import { useQueryClient } from '@tanstack/react-query'

export const useOpenMeet = (courseId?: string) => {
    const { _id } = useToken()
    const queryClient = useQueryClient()
    const { t } = useTranslation('dashboard')
    const { showToast, showError } = useUIContext()
    const { currentEvent, invalidateTimeLine } = useTimeline(courseId)
    const { getCourseData } = useUserCourses()
    const course = getCourseData(courseId)
    const [isOpening, setIsOpening] = useState(false)

    const openMeet = useCallback(async (): Promise<Window | null> => {
        const toastId = 'open-meet-toast' as const
        try {
            showToast(t('Opening live session...'), { id: toastId })
            setIsOpening(true)

            if (!course?.meetings || !courseId) throw new Error('Course ID and meetings are required')
            const { algorithmId, meetings } = course

            let meetUrl: string | null = null
            let { room } = currentEvent ?? {}

            // Handle the case when currentEvent is undefined. Eg. when the class starts
            if (!currentEvent) {
                if (meetings.general) room = 'general'
                else if (meetings.team) room = 'teams'
                else {
                    showToast(t('Wait a moment while we generate the teams'), { id: toastId })
                    return null
                }
            }

            const provider = room === 'teams' ? meetings.team?.provider : meetings.general?.provider

            switch (provider) {
                case 'egg':
                case 'zoom-hybrid':
                case undefined: // This handle some special cases where the provider is not defined
                    const { general_room, teams_room } = await getMeetingTokens(algorithmId)
                    const data = room === null ? null : room === 'teams' ? teams_room : general_room ?? teams_room
                    if (!data) return null

                    meetUrl = data.url ?? null
                    break

                default:
                    meetUrl = meetings.general?.url ?? null
                    break
            }

            if (!meetUrl) {
                showError(t('No meeting link found, please try again later'), { id: toastId })
                return null
            }

            const meetWindow = window.open(meetUrl, '_blank')
            if (meetWindow === null) {
                showError(t('Please allow pop-ups for this site to join the meeting', { id: toastId }))
                return null
            }
            meetWindow.focus()
            return meetWindow
        } catch (error) {
            invalidateTimeLine()
            queryClient.invalidateQueries({
                queryKey: ['User', _id],
                exact: false,
            })
            showError(t('An error occurred while trying to open the meeting'), { id: toastId })
            console.error(error)
            return null
        } finally {
            setIsOpening(false)
        }
    }, [showToast, t, course, courseId, currentEvent, showError, invalidateTimeLine, queryClient, _id])

    return { openMeet, isOpening }
}
